import { useEffect } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";

export interface ModalProps {
  open: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
  /** Optional footer rendered with buttons aligned right. */
  footer?: React.ReactNode;
  /** Width preset. Default: md. */
  size?: "sm" | "md" | "lg";
}

const SIZE: Record<NonNullable<ModalProps["size"]>, string> = {
  sm: "max-w-[420px]",
  md: "max-w-[600px]",
  lg: "max-w-[820px]",
};

export function Modal({ open, onClose, title, children, footer, size = "md" }: ModalProps) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;
  const root = document.getElementById("modal-root") ?? document.body;

  return createPortal(
    <div
      className="fixed inset-0 z-[100] grid place-items-center bg-[rgba(8,9,11,0.55)] backdrop-blur-[2px]"
      onClick={onClose}
      role="presentation"
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={title}
        onClick={(e) => e.stopPropagation()}
        className={`card flex w-full ${SIZE[size]} mx-4 max-h-[calc(100vh-48px)] flex-col overflow-hidden shadow-e3`}
      >
        <header className="flex items-start justify-between border-b border-border-subtle px-5 py-4">
          <h2 className="text-[16px] font-semibold tracking-[-0.01em] text-primary">{title}</h2>
          <button className="iconbtn" onClick={onClose} aria-label="Close">
            <X size={16} />
          </button>
        </header>
        <div className="flex-1 overflow-auto px-5 py-4">{children}</div>
        {footer && (
          <footer className="flex justify-end gap-2 border-t border-border-subtle bg-surface px-5 py-3">
            {footer}
          </footer>
        )}
      </div>
    </div>,
    root,
  );
}
